import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSystem } from '@ohif/core';
import { Icons } from '@ohif/ui-next';
import { DataTable, Pagination, TableColumn } from '../../components/common';
import FormSelectVirtualized from '../../components/common/FormSelectVirtualized';
import EmptyState from '../../components/common/EmptyState';
import ChangePasswordModal from '../../components/common/ChangePasswordModal';
import userService from '../../services/userService';
import authService from '../../auth/authService';
import { mapApiResponseToTableFormat } from '../../utils/userMapper';
import { useUserFilterOptions } from '../../hooks/useUserFilterOptions';
import type { User, UserTableRow, UserPaginationInfo } from '../../types/user.types';
import './UserManagementContent.css';

interface UserManagementContentProps {
  facilityId?: string;
  facilityCode?: string;
  facilityName?: string;
}

function resolveFacilityIdForUserFilter(
  facilityId: string | undefined,
  isRisAdmin: boolean,
  effectiveFacilityId: string | undefined
): string | undefined {
  if (facilityId) {
    return facilityId;
  }
  if (isRisAdmin && effectiveFacilityId) {
    return effectiveFacilityId;
  }
  return undefined;
}

function buildUserListFilters(args: {
  roleFilter: string;
  statusFilter: string;
  nameFilter: string;
  employeeIdFilter: string;
  phoneNumberFilter: string;
}):
  | {
      name?: string;
      role?: string;
      employee_id?: string;
      phone_number?: string;
      status?: string;
    }
  | undefined {
  const filters: {
    name?: string;
    role?: string;
    employee_id?: string;
    phone_number?: string;
    status?: string;
  } = {};
  if (args.roleFilter) {
    filters.role = args.roleFilter;
  }
  if (args.statusFilter) {
    filters.status = args.statusFilter;
  }
  if (args.nameFilter) {
    filters.name = args.nameFilter;
  }
  if (args.employeeIdFilter) {
    filters.employee_id = args.employeeIdFilter;
  }
  if (args.phoneNumberFilter) {
    filters.phone_number = args.phoneNumberFilter;
  }
  return Object.keys(filters).length > 0 ? filters : undefined;
}

const UserManagementContent: React.FC<UserManagementContentProps> = ({
  facilityId,
  facilityCode,
  facilityName = 'Facility',
}) => {
  const navigate = useNavigate();
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;

  const [currentPage, setCurrentPage] = useState(1);
  const [users, setUsers] = useState<User[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [pagination, setPagination] = useState<UserPaginationInfo | null>(null);
  const [nameFilter, setNameFilter] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [employeeIdFilter, setEmployeeIdFilter] = useState<string>('');
  const [phoneNumberFilter, setPhoneNumberFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [selectedUserForPasswordChange, setSelectedUserForPasswordChange] = useState<User | null>(
    null
  );

  // Fetch users from API with pagination and filters
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const currentUser = authService.getUser();
        const storedFacilityId = authService.getFacilityId();
        const facilityFromObject = authService.getFacility();
        const effectiveFacilityId =
          storedFacilityId || (facilityFromObject ? String(facilityFromObject.id) : undefined);
        const isRisAdmin = currentUser?.roles?.some(role => role === 'ris-admin') ?? false;
        const facilityIdForFilter = resolveFacilityIdForUserFilter(
          facilityId,
          isRisAdmin,
          effectiveFacilityId
        );
        const filters = buildUserListFilters({
          roleFilter,
          statusFilter,
          nameFilter,
          employeeIdFilter,
          phoneNumberFilter,
        });

        const response = await userService.getAllUsers(currentPage, facilityIdForFilter, filters);

        if (response.success && response.data) {
          // Show users from API (optionally filtered by facility_id for admins)
          setUsers(response.data);
          if (response.pagination) {
            setPagination(response.pagination);
          }

          // Mark initial load as complete only on first successful load
          setIsInitialLoad(false);

          console.log('UserManagementContent - Users loaded:', {
            totalUsers: response.data.length,
            users: response.data,
            pagination: response.pagination,
            isRisAdmin,
            facilityIdProp: facilityId,
            facilityIdUsed: facilityIdForFilter,
          });
        } else {
          const errorMessage = response.error || 'Failed to load users';
          // Don't clear users on error - keep current data visible

          // Show error notification
          if (uiNotificationService) {
            uiNotificationService.show({
              title: 'Error',
              message: errorMessage,
              type: 'error',
              duration: 5000,
            });
          }
        }
      } catch (err) {
        // Mark initial load as complete even on error
        setIsInitialLoad(false);

        // Only show error, don't hide data (unless it's initial load with no data)
        console.error('UserManagementContent - Error fetching users:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load users';
        // Don't clear users on error - keep current data visible

        if (uiNotificationService) {
          uiNotificationService.show({
            title: 'Error',
            message: errorMessage,
            type: 'error',
            duration: 5000,
          });
        }
      }
    };

    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    nameFilter,
    roleFilter,
    employeeIdFilter,
    phoneNumberFilter,
    statusFilter,
    facilityId,
    uiNotificationService,
  ]);

  // Calculate pagination values from backend or use defaults
  const itemsPerPage = pagination?.usersPerPage || 10;
  const totalItems = pagination?.totalUsers || users.length;

  const handleAddUser = () => {
    navigate('/users/add', {
      state: {
        facilityId,
        facilityName,
      },
    });
  };

  const handleEdit = (user: UserTableRow, index: number) => {
    // Find the original user from the users array
    const originalUser = users.find(u => u.id?.toString() === user.id);
    if (originalUser) {
      navigate('/users/add', {
        state: {
          user: originalUser,
          isEdit: true,
          facilityId,
          facilityName,
        },
      });
    }
  };

  const handleRowClick = (user: UserTableRow, index: number) => {
    // Find the original user from the users array
    const originalUser = users.find(u => u.id?.toString() === user.id);
    if (originalUser) {
      navigate('/users/add', {
        state: {
          user: originalUser,
          isEdit: true,
          facilityId,
          facilityName,
        },
      });
    }
  };

  const handleToggleActive = async (user: UserTableRow, index: number, checked: boolean) => {
    // Find the original user from the users array
    const originalUser = users.find(u => u.id?.toString() === user.id);
    if (!originalUser || !originalUser.id) {
      return;
    }

    // Update user status via API
    const updateData = {
      status: (checked ? 'Active' : 'Inactive') as 'Active' | 'Inactive',
    };

    const response = await userService.updateUser(originalUser.id, updateData);

    if (response.success) {
      // Update local state
      setUsers(prev =>
        prev.map(u =>
          u.id === originalUser.id
            ? { ...u, status: updateData.status as 'Active' | 'Inactive' }
            : u
        )
      );

      if (uiNotificationService) {
        uiNotificationService.show({
          title: 'Success',
          message: response.message || 'User status updated successfully',
          type: 'success',
          duration: 3000,
        });
      }
    } else {
      if (uiNotificationService) {
        uiNotificationService.show({
          title: 'Error',
          message: response.error || 'Failed to update user status',
          type: 'error',
          duration: 5000,
        });
      }
    }
  };

  // Get filter options using API-based search with initial options from current page
  const filterOptions = useUserFilterOptions({ facilityId, users });

  const handleClearFilters = () => {
    setNameFilter('');
    setRoleFilter('');
    setEmployeeIdFilter('');
    setPhoneNumberFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters =
    nameFilter || roleFilter || employeeIdFilter || phoneNumberFilter || statusFilter;

  // Handler for FormSelectVirtualized onChange with toggle functionality
  const createFilterHandler = (currentValue: string, setter: (value: string) => void) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      // If clicking the same option, unselect it
      if (currentValue === newValue) {
        setter('');
      } else {
        setter(newValue);
      }
      setCurrentPage(1); // Reset to first page when filter changes
    };
  };

  const handleChangePassword = useCallback(
    (user: UserTableRow) => {
      const originalUser = users.find(u => u.id?.toString() === user.id);
      if (originalUser) {
        setSelectedUserForPasswordChange(originalUser);
        setChangePasswordModalOpen(true);
      }
    },
    [users]
  );

  const handlePasswordSubmit = useCallback(
    async (newPassword: string) => {
      if (!selectedUserForPasswordChange?.id) {
        throw new Error('User ID is required to change password');
      }

      const response = await authService.updatePassword(
        newPassword,
        selectedUserForPasswordChange.id
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to change password');
      }

      if (uiNotificationService) {
        uiNotificationService.show({
          title: 'Success',
          message: 'Password changed successfully',
          type: 'success',
          duration: 3000,
        });
      }
    },
    [selectedUserForPasswordChange, uiNotificationService]
  );

  // Map users to table format (filters and pagination from backend API)
  const tableUsers = useMemo(() => {
    return users.map((user, index) => {
      const tableData = mapApiResponseToTableFormat(user);
      return {
        ...tableData,
        slNo: (currentPage - 1) * itemsPerPage + index + 1,
        active: user.status === 'Active',
        changePassword: (
          <button
            onClick={e => {
              e.stopPropagation();
              handleChangePassword({
                ...tableData,
                slNo: (currentPage - 1) * itemsPerPage + index + 1,
                active: user.status === 'Active',
              });
            }}
            className="user-management-content-change-password-button"
          >
            Change Password
          </button>
        ),
      };
    });
  }, [users, currentPage, itemsPerPage, handleChangePassword]);

  const columns: TableColumn<UserTableRow>[] = [
    { key: 'slNo', label: 'Sl.No', width: '54px', align: 'left' },
    { key: 'active', label: 'Active', width: '80px', align: 'center' },
    { key: 'fullName', label: 'Employee Name', width: '150px' },
    { key: 'employeeId', label: 'Employee ID', width: '120px' },
    { key: 'phoneNo', label: 'Phone number', width: '140px' },
    { key: 'role', label: 'Role', width: '120px' },
    { key: 'emailId', label: 'Email ID', width: '200px' },
    { key: 'username', label: 'Username', width: '140px' },
    { key: 'createdBy', label: 'Created By', width: '120px' },
    { key: 'createdAt', label: 'Created at', width: '150px' },
    { key: 'lastModifiedBy', label: 'Last Modified By', width: '150px' },
    { key: 'lastModified', label: 'Last Modified', width: '150px' },
    { key: 'changePassword', label: 'PASSWORD', width: '140px', align: 'center' },
  ];

  return (
    <>
      {isInitialLoad && users.length === 0 ? (
        <div className="user-management-content-loading">Loading users...</div>
      ) : users.length === 0 && !hasActiveFilters ? (
        <div className="user-management-content-empty-container">
          <EmptyState
            title="No Users added yet"
            description=""
            actionLabel="Add Users"
            onAction={handleAddUser}
            illustrationType="empty-box"
          />
        </div>
      ) : (
        <div className="user-management-content-container">
          {/* Table Header Section */}
          <div className="user-management-content-header">
            {/* Left Side: Title, Search, Filters */}
            <div className="user-management-content-header-left">
              <h2 className="user-management-content-title">Employees List</h2>
              <div className="user-management-content-filters">
                <div className="user-management-content-filter-wrapper user-management-content-filter-wrapper-name">
                  <FormSelectVirtualized
                    label="Name"
                    options={filterOptions.nameOptions.options}
                    value={nameFilter}
                    onChange={createFilterHandler(nameFilter, setNameFilter)}
                    placeholder="Select Name"
                    name="nameFilter"
                    onSearchChange={filterOptions.nameOptions.setSearchQuery}
                    isLoading={filterOptions.nameOptions.isLoading}
                  />
                </div>
                <div className="user-management-content-filter-wrapper user-management-content-filter-wrapper-role">
                  <FormSelectVirtualized
                    label="Role"
                    options={filterOptions.roleOptions.options}
                    value={roleFilter}
                    onChange={createFilterHandler(roleFilter, setRoleFilter)}
                    placeholder="Select Role"
                    name="roleFilter"
                    onSearchChange={filterOptions.roleOptions.setSearchQuery}
                    isLoading={filterOptions.roleOptions.isLoading}
                  />
                </div>
                <div className="user-management-content-filter-wrapper user-management-content-filter-wrapper-employee-id">
                  <FormSelectVirtualized
                    label="Employee ID"
                    options={filterOptions.employeeIdOptions.options}
                    value={employeeIdFilter}
                    onChange={createFilterHandler(employeeIdFilter, setEmployeeIdFilter)}
                    placeholder="Select Employee ID"
                    name="employeeIdFilter"
                    onSearchChange={filterOptions.employeeIdOptions.setSearchQuery}
                    isLoading={filterOptions.employeeIdOptions.isLoading}
                  />
                </div>
                <div className="user-management-content-filter-wrapper user-management-content-filter-wrapper-phone">
                  <FormSelectVirtualized
                    label="Phone Number"
                    options={filterOptions.phoneNumberOptions.options}
                    value={phoneNumberFilter}
                    onChange={createFilterHandler(phoneNumberFilter, setPhoneNumberFilter)}
                    placeholder="Select Phone"
                    name="phoneNumberFilter"
                    onSearchChange={filterOptions.phoneNumberOptions.setSearchQuery}
                    isLoading={filterOptions.phoneNumberOptions.isLoading}
                  />
                </div>
                <div className="user-management-content-filter-wrapper user-management-content-filter-wrapper-status">
                  <FormSelectVirtualized
                    label="Status"
                    options={filterOptions.statusOptions}
                    value={statusFilter}
                    onChange={createFilterHandler(statusFilter, setStatusFilter)}
                    placeholder="Select Status"
                    name="statusFilter"
                  />
                </div>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="user-management-content-clear-button"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                    }}
                  >
                    <Icons.Close className="h-5 w-5" />
                    <span>Clear All</span>
                  </button>
                )}
              </div>
            </div>
            {/* Right Side: Add New User Button */}
            <div className="user-management-content-header-right">
              <button
                onClick={handleAddUser}
                className="user-management-content-add-button"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 3.33337V12.6667M3.33334 8H12.6667"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Add New User
              </button>
            </div>
          </div>

          <div className="user-management-content-table-container">
            <DataTable
              columns={columns}
              data={tableUsers}
              onRowClick={handleRowClick}
              onEdit={handleEdit}
              onToggleActive={handleToggleActive}
              getRowKey={row => row.id}
              pageScrollStickyHeader
            />
          </div>

          <div className="user-management-content-pagination">
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      )}

      <ChangePasswordModal
        isOpen={changePasswordModalOpen}
        onClose={() => {
          setChangePasswordModalOpen(false);
          setSelectedUserForPasswordChange(null);
        }}
        onSubmit={handlePasswordSubmit}
        title="Change Password"
      />
    </>
  );
};

export default UserManagementContent;
